// LIBRERIE/IMPORTS
import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";

// COMPONENTI
import Pagination from "./Pagination";
import SubHeaderDefault from "../Header/subHeader/SubHeaderDefault";
import ImmobiliGrid from "./ImmobiliGrid";
import ImmobiliList from "./ImmobiliList";

// HOOKS
import { useGetPropertiesSold } from "../../hooks/useGetPropertiesSold";
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// CONFIGURAZIONI
import { AGENCY_ID, IMMOBILI_SHOW_VIEW_TOGGLE, appConfig } from "../../config/appConfig";

// FOGLI DI STILE
import styles from './Immobili.module.css';
//import { Search } from "lucide";

const ImmobiliSuccess: React.FC = () => {

    const [currentPage, setCurrentPage] = useState(1);
    const [propertiesPerPage] = useState(9);
    const [orderBy, setOrderBy] = useState<string>("sellDate,desc");
    const [currentView, setCurrentView] = useState<'grid' | 'list'>(appConfig.ui.immobiliPage.defaultView);
    const location = useLocation();
    const { t } = useTranslation();

    const queryParams = new URLSearchParams(location.search);
    const contract = queryParams.getAll('contratto').map(v => v.trim()).filter(v => v !== "").map(v => parseInt(v, 10)).filter(v => !isNaN(v));
    const tipologia = queryParams.getAll('tipologia').map(v => parseInt(v, 10)).filter(v => !isNaN(v));
    const frazione = queryParams.getAll('frazione');
    const provincia = String(queryParams.get('provincia') || '');
    const comuni = queryParams.getAll('comune');
    const maxRooms = Number(queryParams.get('camere_max') || 0);
    const maxBathrooms = Number(queryParams.get('bagni_max') || 0);
    const minPrice = Number(queryParams.get('prezzo_min') || 0);
    const maxPrice = Number(queryParams.get('prezzo_max') || 0);
    const sqmtMin = Number(queryParams.get('mq_min') || 0);
    const sqmtMax = Number(queryParams.get('mq_max') || 0);
    const gardenPrivate = queryParams.get('gardenPrivate') || 0;
    const terrace = queryParams.get('terrace') || 0;
    const swimmingPool = queryParams.get('swimmingPool') || 0;
    const ref = queryParams.get('ref') || '';
    const orderByParts = orderBy.split(",");
    const memoizedFilters = useMemo(() => ({
        contract: contract || [], type: tipologia || [], provincia: provincia || '', comune: comuni || [], frazione: frazione || [], maxRooms: maxRooms || 0, maxBathrooms: maxBathrooms || 0, minPrice: minPrice || 0, maxPrice: maxPrice || 0, sqmtMin: sqmtMin || 0, sqmtMax: sqmtMax || 0, gardenPrivate: Boolean(gardenPrivate) || false, terrace: Boolean(terrace) || false, swimmingPool: Boolean(swimmingPool) || false, ref: ref || '',
    }), [contract, tipologia, provincia, comuni, frazione, maxRooms, maxBathrooms, minPrice, maxPrice, sqmtMin, sqmtMax, gardenPrivate, terrace, swimmingPool, ref]);


    const { properties, totalCount, loading, error } = useGetPropertiesSold(AGENCY_ID, {
        limit: 100000000,
        offset: 0,
        withCount: true,
        filters: memoizedFilters,
        orderBy: {
            field: orderByParts[0] || 'sellDate',
            direction: (orderByParts[1] as 'desc' | 'asc') || 'desc',
        },
        portal: 56,
    });

    const options = [
        { value: "sellDate,desc", label: t('propertyList.sortOptions.sellDateDesc') },
        { value: "sellDate,asc", label: t('propertyList.sortOptions.sellDateAsc') },
        { value: "insertionDate,desc", label: t('propertyList.sortOptions.dateDesc') },
        { value: "insertionDate,asc", label: t('propertyList.sortOptions.dateAsc') },
        { value: "price,desc", label: t('propertyList.sortOptions.priceDesc') },
        { value: "price,asc", label: t('propertyList.sortOptions.priceAsc') },
    ];

    const totalPages = totalCount > 0 ? Math.ceil(totalCount / propertiesPerPage) : Math.ceil(properties.length / propertiesPerPage);

    const startIndex = (currentPage - 1) * propertiesPerPage;
    const currentProperties = useMemo(() => {
        if (!loading && !error) {
            return properties.slice(startIndex, startIndex + propertiesPerPage);
        }
        return [];
    }, [properties, propertiesPerPage, loading, error, startIndex]);


    const { agency } = useGetAgencyDetails(AGENCY_ID);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo(0, 0);
        }
    };

    const ViewComponent = currentView === 'list' ? ImmobiliList : ImmobiliGrid;
    const pageTitle = t('propertySuccess.pageTitle');
    const agencyName = agency?.agencyName ?? t('common.defaultAgencyName');
    const helmetTitle = t('common.pageTitlePattern', { pageTitle: pageTitle, agencyName: agencyName });

    //Stile select
    const customStyles = {
        control: (base: any, state: any) => ({
            ...base,
            border: state.isFocused ? "1px solid #212349 !important" : "",
            boxShadow: "none",
            width: "200px"
        }),
        placeholder: (base: any) => ({
            ...base,
            color: "#000"
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isFocused ? "#eee" : "",
            color: state.isSelected ? "#000" : "#000",
            fontWeight: state.isSelected ? "bold" : "",

        })
    }

    return (
        <>
            <Helmet>
                <title>{helmetTitle}</title>
            </Helmet>

            <SubHeaderDefault title={pageTitle} isSeo={true} />

            <main className="space-ptb">
                <div className="container-fluid">
                    <div className="row align-items-center mb-4">
                        <div className="col-auto">
                            <h3>{loading ? t('common.loading') : `${totalCount} ${t('propertyList.foundLabel')}`}</h3>
                        </div>
                        <div className="col">
                            <div className="property-filter d-sm-flex align-items-center justify-content-between flex-wrap">
                                {/* ... Dropdown Ordinamento ... */}
                                <ul className="property-short list-unstyled d-sm-flex mb-0 align-items-center">
                                    <li>
                                        <form className="form-inline">
                                            <div className="form-group d-md-flex d-block align-items-center pb-sm-2">
                                                <label className="justify-content-start mr-2">{t('common.orderBy')}</label>
                                                <div className="short-by">
                                                    <Select
                                                        id="alternativeOrder"
                                                        value={options.find((option) => option.value === orderBy)}
                                                        options={options}
                                                        onChange={(e) => setOrderBy(e?.value as string)}
                                                        isSearchable={false}
                                                        styles={customStyles}
                                                        placeholder={t('common.select')}
                                                    />
                                                </div>
                                            </div>
                                        </form>
                                    </li>
                                </ul>

                                {IMMOBILI_SHOW_VIEW_TOGGLE && (
                                    <div className="property-view-toggle pr-2">
                                        <button
                                            type="button"
                                            className={`${styles.viewToggleButton} ${currentView === 'grid' ? styles.active : ''}`}
                                            onClick={() => setCurrentView('grid')}
                                            aria-label={t('propertyList.viewToggle.gridLabel') || 'Visualizzazione Griglia'}
                                            aria-pressed={currentView === 'grid'}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-th" aria-hidden="true"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.viewToggleButton} ${currentView === 'list' ? styles.active : ''}`}
                                            onClick={() => setCurrentView('list')}
                                            aria-label={t('propertyList.viewToggle.listLabel') || 'Visualizzazione Lista'}
                                            aria-pressed={currentView === 'list'}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-list" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row">

                        {/* Risultati */}
                        <div className="col-lg-12 seller-listing">

                            {!loading && !error && (
                                <div className="row">
                                    <ViewComponent
                                        properties={currentProperties}
                                        loading={false}
                                        error={null}
                                        basePath="immobili-successi"
                                        labelSold={true}
                                    />
                                </div>
                            )}

                            {!loading && error && (
                                <div className="alert alert-danger">{t('propertyList.errorLoadingProperties')}</div>
                            )}

                            {/* Paginazione */}
                            {totalCount > propertiesPerPage && !error && (
                                <div className="row mt-4">
                                    <div className="col-12">
                                        <Pagination
                                            count={totalCount || 0}
                                            limit={propertiesPerPage}
                                            currentPage={currentPage}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default ImmobiliSuccess;